import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnInit, Output, OnChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ControlContainer, UntypedFormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { IGlobalProblemSubtype } from '../../../../interfaces/global-problem-subtype.interface';
import { CreateGlobalChecklistDialogComponent } from '../create-global-checklist-dialog/create-global-checklist-dialog.component';
import { IChecklist } from '../../../../interfaces/checklist.interface';
import { HDTicketTypesToken } from '../../check-lists.constant';
import { CreateChecklistParams } from '../check-lists/check-lists.component';

@Component({
  selector: 'app-check-lists-global-problem',
  templateUrl: './check-lists-global-problem.component.html',
  styleUrls: ['./check-lists-global-problem.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckListsGlobalProblemComponent implements OnInit, OnChanges {
  @Input() gpSubtypes: IGlobalProblemSubtype[] = [];
  @Input() checklists: IChecklist[] = [];

  @Output() created: EventEmitter<CreateChecklistParams> = new EventEmitter<CreateChecklistParams>();
  @Output() selected: EventEmitter<IChecklist> = new EventEmitter<IChecklist>();
  @Output() changed: EventEmitter<void> = new EventEmitter<void>();

  selectedGPChecklist: IChecklist;
  selectedGPSubtypes: IGlobalProblemSubtype[] = [];
  transformedGpSubtypes: IGlobalProblemSubtype[] = [];
  form: UntypedFormGroup;

  cloneDeep = cloneDeep;

  constructor(
    private modalService: NgbModal,
    private controlContainer: ControlContainer,
    private injector: Injector
  ) {}

  ngOnInit() {
    this.form = this.controlContainer.control as UntypedFormGroup;
    this.transformedGpSubtypes = this.transformGPSubtypes(this.gpSubtypes);
  }

  // При изменении входных данных обновляем трансформированные данные
  ngOnChanges(changes) {
    if (changes.gpSubtypes && changes.gpSubtypes.currentValue) {
      // При изменении списка подтипов обновляем трансформированные данные
      this.transformedGpSubtypes = this.transformGPSubtypes(changes.gpSubtypes.currentValue);

      // Если есть выбранный чек-лист, обновляем выбранные подтипы
      if (this.selectedGPChecklist && this.selectedGPChecklist.gp_subtypes) {
        this.selectedGPSubtypes = this.selectedGPChecklist.gp_subtypes.map((subtypeId) => this.gpSubtypes.find((tt) => tt.id === subtypeId)).filter(Boolean);

        // Обновляем флаги checked
        this.transformedGpSubtypes = this.transformedGpSubtypes.map((tt) => ({
          ...tt,
          checked: this.selectedGPSubtypes.some((selected) => selected.id === tt.id),
        }));
      }
      this.transformedGpSubtypes.sort((a, b) => this.sortBySelected(a, b));
    }
  }

  // Трансформируем данные для правильного отображения в мультикомбобоксе
  transformGPSubtypes(subtypes: (IGlobalProblemSubtype & { checked?: boolean })[]): any[] {
    if (!subtypes || !subtypes.length) {
      return [];
    }

    const subtypeGroups = new Map<string, (IGlobalProblemSubtype & { checked?: boolean })[]>();
    subtypes.forEach((subtype) => {
      const key = subtype.subtype;
      if (!subtypeGroups.has(key)) {
        subtypeGroups.set(key, []);
      }
      subtypeGroups.get(key).push(subtype);
    });

    const uniqueSubtypes = Array.from(subtypeGroups.values()).map((group) => {
      return group.find((item) => item.checked) || group[0];
    });

    return uniqueSubtypes.map((subtype) => ({
      ...subtype,
      name: subtype.subtype,
    }));
  }

  sortBySelected(a: any, b: any): number {
    if (a.checked !== b.checked) {
      return a.checked ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  }

  handleChangeSubtype(gpSubtypes: IGlobalProblemSubtype[]): void {
    if (!gpSubtypes) {
      this.selectedGPSubtypes = [];
      this.form.patchValue({
        gpSubtypes: [],
      });
      this.changed.emit();
      return;
    }

    this.selectedGPSubtypes = gpSubtypes;

    // Обновляем флаги checked в исходных данных
    this.transformedGpSubtypes = this.transformedGpSubtypes.map((tt) => ({
      ...tt,
      checked: gpSubtypes.some((selected) => selected.id === tt.id),
    }));
    this.transformedGpSubtypes.sort((a, b) => this.sortBySelected(a, b));

    this.form.patchValue({
      gpSubtypes: gpSubtypes.map((tt) => tt && tt.id).filter((id) => id !== undefined),
    });
    this.changed.emit();
  }

  handleChangeGPChecklist(selectedGPChecklist: IChecklist): void {
    if (!selectedGPChecklist) {
      this.selectedGPChecklist = null;
      this.selectedGPSubtypes = [];
      // Очищаем все флаги checked и обновляем трансформированные данные
      this.transformedGpSubtypes = this.transformedGpSubtypes.map((tt) => ({
        ...tt,
        checked: false,
      }));
      this.transformedGpSubtypes.sort((a, b) => this.sortBySelected(a, b));
      this.form.patchValue({
        gpSubtypes: [],
      });
      return;
    }

    // Сначала очищаем все флаги checked
    this.transformedGpSubtypes = this.transformedGpSubtypes.map((tt) => ({
      ...tt,
      checked: false,
    }));

    this.selectedGPChecklist = selectedGPChecklist;
    this.selected.emit(selectedGPChecklist);

    // Находим подтипы, которые относятся к выбранному чек-листу
    this.selectedGPSubtypes =
      selectedGPChecklist && selectedGPChecklist.gp_subtypes
        ? selectedGPChecklist.gp_subtypes.map((subtypeId) => this.gpSubtypes.find((tt) => tt.id === subtypeId)).filter(Boolean)
        : [];

    // Устанавливаем флаг checked для подтипов выбранного чек-листа
    this.transformedGpSubtypes = this.transformedGpSubtypes.map((tt) => ({
      ...tt,
      checked: this.selectedGPSubtypes.some((selected) => selected.id === tt.id),
    }));
    this.transformedGpSubtypes.sort((a, b) => this.sortBySelected(a, b));

    this.form.patchValue({
      gpSubtypes: this.selectedGPSubtypes.map((tt) => tt.id),
    });
  }

  openCreateGlobalChecklist(): void {
    const clearedSubtypes = Array.isArray(this.gpSubtypes)
      ? cloneDeep(this.gpSubtypes).map((tt) => ({
          ...tt,
          checked: false,
        }))
      : [];

    const createDialog = this.modalService.open(CreateGlobalChecklistDialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
      injector: Injector.create({
        providers: [
          {
            provide: HDTicketTypesToken,
            useValue: clearedSubtypes,
          },
        ],
        parent: this.injector,
      }),
    });

    createDialog.result.then((result?: CreateChecklistParams) => {
      if (!result) {
        return;
      }
      this.created.emit(result);
    });
  }
}
